"use client"

import { entitledTierLabel } from "@/lib/entitlements"
import { useState, useEffect, useRef, useCallback, memo } from "react"
import { useRouter } from "next/navigation"
import { useScriptStore, type UserScript, type CommunityScript as LocalCommunityScript } from "@/lib/script-store"
import { formatPrice, type CommunityScript as MarketScript } from "@/lib/marketplace-store"
import { useAuthStore } from "@/lib/auth-store"
import { FREE_SCRIPT_LIMIT, isPremiumTier } from "@/lib/script-limits"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import { Play, Pause, Star, Plus, SlidersHorizontal, ScrollText, ShoppingCart, Store, HeartHandshake, ShoppingBag, Loader2, ExternalLink, PlayCircle, Trophy, Heart } from "@/lib/icons"
import { HeatmapStrip } from "@/components/ui/heatmap-strip"
import { cn } from "@/lib/utils"
import { contentTypeDetector } from "@/lib/content-type-detector"
import { CommunityScriptDetailModal } from "@/components/marketplace/community-script-detail-modal"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import { useScriptLinkingStore } from "@/lib/script-linking-store"
import type { BooruPost } from "@/lib/booru-api"
import { useTranslations } from "next-intl"

const BOORU_SCRIPT_PLAY_OPTIONS = { skipHSSP: false } as const
const BOORU_SCRIPT_SET_OPTIONS = { skipPrewarm: true } as const

// Isolated seeker bar — owns its own time subscription so the parent never re-renders on timeupdate
const ScriptSeeker = memo(function ScriptSeeker({ scriptId }: { scriptId: string }) {
  const currentTime = useGlobalPlaybackStore((s) => s.currentTime)
  const duration = useGlobalPlaybackStore((s) => s.duration)
  const currentScriptId = useGlobalPlaybackStore((s) => s.currentScript?.id ?? null)
  if (currentScriptId !== scriptId) return null
  return (
    <div className="relative h-[3px] bg-muted/40 overflow-hidden">
      <div
        className="absolute inset-y-0 left-0 bg-primary"
        style={{ width: `${duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0}%` }}
      />
    </div>
  )
})

interface ContentScriptsPanelProps {
  contentId: string
  contentSite: string
  contentUrl: string
  contentType: "video" | "image"
  thumbnailUrl?: string
  onPlay: (script: UserScript | LocalCommunityScript) => void
  onPlayMarketplaceInline?: (script: MarketScript, options?: { autoStart?: boolean }) => void
  onCreateNew: () => void
  onRequestBounty?: () => void
  onManualControl: () => void
  onAddToVerticalEditor?: () => void
  post?: BooruPost
  activeMarketplaceScriptId?: string | null
  /** Pass the video element ref so the panel can trigger video.play/pause */
  videoRef?: React.RefObject<HTMLVideoElement | null>
  /** Shared suppress ref — panel sets flags here before calling video.play/pause so the parent's sync listener knows to skip */
  videoSyncSuppressRef?: React.MutableRefObject<{ play: boolean; pause: boolean }>
  // Controlled tab — lifted to parent so it survives remounts
  activeTab?: string
  onActiveTabChange?: (tab: string) => void
}

interface ContentBountyRequest {
  id: string
  title: string
  reward_cents: number
  application_count?: number
}

export function ContentScriptsPanel({
  contentId,
  contentSite,
  contentUrl,
  contentType,
  thumbnailUrl,
  onPlay,
  onPlayMarketplaceInline,
  onCreateNew,
  onRequestBounty,
  onManualControl,
  post,
  activeMarketplaceScriptId,
  videoRef,
  videoSyncSuppressRef,
  activeTab: controlledActiveTab,
  onActiveTabChange,
}: ContentScriptsPanelProps) {
  const t = useTranslations("contentScriptsPanel")
  const router = useRouter()
  const { getScriptsForContent, isBookScript, scripts: allUserScripts, favoriteScriptIds, toggleFavoriteScript } =
    useScriptStore()
  const profile = useAuthStore((s) => s.profile)
  const userId = useAuthStore((s) => s.user?.id)
  const userIsPremium = isPremiumTier(entitledTierLabel(profile as any))
  const totalScriptCount = allUserScripts.length
  const scriptUsageBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scriptUsageBarRef.current) {
      scriptUsageBarRef.current.style.width = `${Math.min(100, (totalScriptCount / FREE_SCRIPT_LIMIT) * 100)}%`
    }
  }, [totalScriptCount])

  // Global playback store
  const setGlobalScript = useGlobalPlaybackStore((s) => s.setScript)
  const globalPlay = useGlobalPlaybackStore((s) => s.play)
  const globalPause = useGlobalPlaybackStore((s) => s.pause)
  const globalCurrentScriptId = useGlobalPlaybackStore((s) => s.currentScript?.id ?? null)
  const globalIsPlaying = useGlobalPlaybackStore((s) => s.isPlaying)

  // Refs to avoid stale closures in callbacks
  const globalIsPlayingRef = useRef(false)
  const globalCurrentScriptIdRef = useRef<string | null>(null)
  useEffect(() => { globalIsPlayingRef.current = globalIsPlaying }, [globalIsPlaying])
  useEffect(() => { globalCurrentScriptIdRef.current = globalCurrentScriptId }, [globalCurrentScriptId])

  const setScriptLinkingState = useScriptLinkingStore.setState

  const [internalActiveTab, setInternalActiveTab] = useState("marketplace")
  const activeTab = controlledActiveTab ?? internalActiveTab
  const setActiveTab = (tab: string) => {
    setInternalActiveTab(tab)
    onActiveTabChange?.(tab)
  }
  const [marketScripts, setMarketScripts] = useState<MarketScript[]>([])
  const [ownedMarketScripts, setOwnedMarketScripts] = useState<MarketScript[]>([])
  const [loadingMarket, setLoadingMarket] = useState(false)
  const [bountyRequests, setBountyRequests] = useState<ContentBountyRequest[]>([])
  const [detailScript, setDetailScript] = useState<MarketScript | null>(null)
  const [activeInlineScriptId, setActiveInlineScriptId] = useState<string | null>(null)
  const [inlineLoading, setInlineLoading] = useState<string | null>(null)
  const [fullPlayerLoadingId, setFullPlayerLoadingId] = useState<string | null>(null)
  // Track which scripts user has purchased (checked lazily via ownership API)
  const [purchasedIds, setPurchasedIds] = useState<Set<string>>(new Set())

  const scriptMatchesCurrentContent = (script: Partial<MarketScript> & Record<string, any>) => {
    const rawId = String(script.linked_content_id ?? "")
    const rawSite = String(script.linked_content_site ?? "")
    const variants = new Set([rawId, rawId.includes(":") ? rawId.split(":").slice(1).join(":") : rawId].filter(Boolean))
    const siteFromId = rawId.includes(":") ? rawId.split(":")[0] : ""
    return variants.has(contentId) && (!contentSite || rawSite === contentSite || siteFromId === contentSite)
  }

  // Fetch marketplace scripts linked to this content
  useEffect(() => {
    if (!contentId || !contentSite) return
    setLoadingMarket(true)
    fetch(`/api/marketplace/scripts?linkedContentId=${encodeURIComponent(contentId)}&linkedContentSite=${encodeURIComponent(contentSite)}&sortBy=popular&limit=20`)
      .then((r) => r.ok ? r.json() : { scripts: [] })
      .then((data) => setMarketScripts(data.scripts || []))
      .catch(() => setMarketScripts([]))
      .finally(() => setLoadingMarket(false))
  }, [contentId, contentSite])

  useEffect(() => {
    if (!contentId || !contentSite || !userId) {
      setOwnedMarketScripts([])
      return
    }

    let cancelled = false
    async function fetchOwnedScriptsForContent() {
      const byId = new Map<string, MarketScript>()

      const addScript = (script: any) => {
        if (!script?.id || !scriptMatchesCurrentContent(script)) return
        byId.set(script.id, script as MarketScript)
      }

      try {
        const ownRes = await fetch("/api/marketplace/scripts?creatorOwn=true")
        if (ownRes.ok) {
          const ownData = await ownRes.json()
          ;(ownData.scripts || []).forEach(addScript)
        }
      } catch {}

      try {
        const libraryRes = await fetch("/api/marketplace/library")
        if (libraryRes.ok) {
          const libraryData = await libraryRes.json()
          ;(libraryData.library || []).forEach((entry: any) => {
            if (entry.script) addScript({ ...entry.script, library_entry: entry })
          })
        }
      } catch {}

      if (!cancelled) setOwnedMarketScripts(Array.from(byId.values()))
    }

    fetchOwnedScriptsForContent()
    return () => { cancelled = true }
  // scriptMatchesCurrentContent is intentionally derived from current content props.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, contentSite, userId])

  useEffect(() => {
    if (!contentId || !contentSite) return
    fetch(`/api/bounties?content_id=${encodeURIComponent(contentId)}&content_site=${encodeURIComponent(contentSite)}&limit=5`)
      .then((r) => r.ok ? r.json() : { bounties: [] })
      .then((data) => setBountyRequests(data.bounties || []))
      .catch(() => setBountyRequests([]))
  }, [contentId, contentSite])

  // Check ownership for paid scripts in batch
  useEffect(() => {
    if (marketScripts.length === 0) return
    const paidIds = marketScripts.filter((s) => s.price_cents > 0).map((s) => s.id)
    if (paidIds.length === 0) return
    Promise.all(
      paidIds.map((id) =>
        fetch(`/api/marketplace/scripts/${id}/ownership`)
          .then((r) => r.ok ? r.json() : { owned: false })
          .then((d) => d.owned ? id : null)
          .catch(() => null)
      )
    ).then((results) => {
      const owned = new Set(results.filter(Boolean) as string[])
      setPurchasedIds(owned)
    })
  }, [marketScripts])

  // Local scripts from the script store
  const fullContentId = `${contentSite}:${contentId}`
  const allContentScripts = getScriptsForContent(fullContentId)
  const isCover = post ? contentTypeDetector.isCover(post) : false
  const localScripts = allContentScripts.filter((script) => isCover || !isBookScript(script))
  // Scripts the user published to the marketplace for this content — these belong to them
  // just as much as local drafts, so "My Scripts" should surface them too.
  const ownMarketScripts = ownedMarketScripts
  const ownedMarketIds = new Set(ownMarketScripts.map((s) => s.id))
  const publicMarketScripts = marketScripts.filter((s) => !ownedMarketIds.has(s.id) && (!userId || s.creator_id !== userId))

  const buildUserScript = (script: MarketScript, playableScript: any): UserScript => {
    const actions = playableScript.actions ?? []
    const dur = actions.length > 0 ? actions[actions.length - 1].at : 0
    return {
      id: script.id,
      name: script.title,
      description: script.description ?? "",
      contentUrl: contentUrl || script.linked_content_url || undefined,
      contentSite: contentSite || script.linked_content_site || undefined,
      contentType,
      thumbnailUrl: script.thumbnail_url ?? thumbnailUrl,
      funscript: {
        version: playableScript.version ?? "1.0",
        inverted: false,
        range: 100,
        actions,
        metadata: { ...(playableScript.metadata ?? {}), title: script.title, duration: dur },
      },
      isPublished: true,
      tags: script.tags ?? [],
      deviceCompatibility: [],
      averageIntensity: script.average_intensity ?? 50,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
  }

  const playOpts = BOORU_SCRIPT_PLAY_OPTIONS
  const setScriptOpts = BOORU_SCRIPT_SET_OPTIONS

  const handleTogglePlayPause = useCallback(async () => {
    if (!globalCurrentScriptIdRef.current) return
    if (globalIsPlayingRef.current) {
      globalPause()
      if (videoRef?.current) {
        if (videoSyncSuppressRef) videoSyncSuppressRef.current.pause = true
        videoRef.current.pause()
      }
    } else {
      try {
        await globalPlay(playOpts)
      } catch {
        return
      }
      if (videoRef?.current) {
        if (videoSyncSuppressRef) videoSyncSuppressRef.current.play = true
        videoRef.current.play().catch(() => {})
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalPause, globalPlay, videoRef, videoSyncSuppressRef])

  const handlePlayHere = useCallback(async (script: MarketScript) => {
    // If this card is already the active script, toggle play/pause
    if (activeInlineScriptId === script.id && globalCurrentScriptId === script.id) {
      handleTogglePlayPause()
      return
    }
    if (inlineLoading) return
    setActiveInlineScriptId(script.id)
    setInlineLoading(script.id)
    try {
      const res = await fetch(`/api/marketplace/scripts/${script.id}/play`)
      if (!res.ok) throw new Error(t("failedToLoad"))
      const data = await res.json()
      const playableScript = data.script ?? data
      const userScript = buildUserScript(script, playableScript)
      setGlobalScript(userScript, setScriptOpts)
      await globalPlay(playOpts)
      if (videoRef?.current) {
        if (videoSyncSuppressRef) videoSyncSuppressRef.current.play = true
        videoRef.current.play().catch(() => {})
      }
    } catch {
      setActiveInlineScriptId(null)
    } finally {
      setInlineLoading(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInlineScriptId, globalCurrentScriptId, handleTogglePlayPause, inlineLoading, setGlobalScript, globalPlay, videoRef, videoSyncSuppressRef])

  const handleOpenFullPlayer = async (script: MarketScript) => {
    setFullPlayerLoadingId(script.id)
    try {
      const res = await fetch(`/api/marketplace/scripts/${script.id}/play`)
      if (res.ok) {
        const data = await res.json()
        const playableScript = data.script ?? data
        const userScript = buildUserScript(script, playableScript)
        setGlobalScript(userScript)
        setScriptLinkingState({
          returnToEditor: true,
          returnToTab: "global-script-player",
          isLinkingMode: false,
        })
      }
    } catch {
    } finally {
      setFullPlayerLoadingId(null)
    }
    router.push("/?tab=scripter&editor=script-player")
  }

  const totalCount = publicMarketScripts.length + localScripts.length + ownMarketScripts.length
  const effectiveActiveInlineScriptId = activeMarketplaceScriptId ?? activeInlineScriptId

  return (
    <>
      <div className="space-y-3">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full px-1">
            <TabsTrigger value="marketplace" className="flex-1 min-w-0 gap-1 text-xs overflow-hidden px-1">
              <Store className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{t("scriptsValue", { value: publicMarketScripts.length > 0 ? ` (${publicMarketScripts.length})` : "" })}</span>
            </TabsTrigger>
            <TabsTrigger value="local" className="flex-1 min-w-0 gap-1 text-xs overflow-hidden px-1">
              <ScrollText className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t("myScriptsValue", { value: (localScripts.filter(s => !s.isPublished).length + ownMarketScripts.length) > 0 ? ` (${localScripts.filter(s => !s.isPublished).length + ownMarketScripts.length})` : "" })}</span>
            </TabsTrigger>
            <TabsTrigger value="manual" className="flex-1 min-w-0 gap-1 text-xs overflow-hidden px-1">
              <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t("device")}</span>
            </TabsTrigger>
          </TabsList>

          {/* Marketplace Scripts Tab */}
          <TabsContent value="marketplace" className="mt-3">
            {loadingMarket ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-lg border">
                    <Skeleton className="h-12 w-16 rounded flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : publicMarketScripts.length === 0 ? (
              <div className="text-center py-8 px-2">
                <ShoppingCart className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                <p className="text-sm text-muted-foreground mb-1">{t("noScriptsForThisContentYet")}</p>
                <p className="text-xs text-muted-foreground mb-4">{t("beTheFirstToCreateOneOrRequestItFromT")}</p>
                {bountyRequests.length > 0 && (
                  <div className="mb-4 space-y-2 text-left">
                    {bountyRequests.map((bounty) => (
                      <div key={bounty.id} className="rounded-lg border bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <Trophy className="h-3.5 w-3.5 text-yellow-500" />
                          <span className="truncate">{bounty.title}</span>
                          <Badge variant="outline" className="ml-auto text-[10px]">
                            {bounty.reward_cents > 0 ? `$${(bounty.reward_cents / 100).toFixed(2)}` : "Free"}
                          </Badge>
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {t("requestAlreadyPostedSuffix", { suffix: typeof bounty.application_count === "number" ? ` · ` + t("countApplicants", { count: bounty.application_count }) : "" })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button size="sm" onClick={onCreateNew} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    {t("createScript")}
                  </Button>
                  {onRequestBounty && (
                    <Button size="sm" onClick={onRequestBounty} variant="secondary">
                      <HeartHandshake className="h-5 w-5 mr-2" />
                      {t("requestAScript")}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <ScrollArea className="max-h-64 w-full">
                <div className="divide-y rounded-xl border bg-background/30 overflow-hidden w-full">
                  {publicMarketScripts.map((script) => {
                    const isFree = script.price_cents === 0
                    const isOwnScript = userId != null && script.creator_id === userId
                    const isPurchased = isFree || isOwnScript || purchasedIds.has(script.id)
                    const isActiveInline = effectiveActiveInlineScriptId === script.id
                    const isOpeningFullPlayer = fullPlayerLoadingId === script.id
                    return (
                      <div
                        key={script.id}
                        className={cn(
                          "flex flex-col w-full overflow-hidden transition-colors hover:bg-accent/35",
                          isActiveInline && "bg-primary/10"
                        )}
                      >
                        <div className="flex items-center gap-2 px-2 py-2">
                          {/* Thumbnail */}
                          <div className="h-10 w-10 shrink-0 rounded overflow-hidden bg-muted">
                            {script.thumbnail_url ? (
                              <img src={script.thumbnail_url} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <ScrollText className="h-3.5 w-3.5 text-muted-foreground/40" />
                              </div>
                            )}
                          </div>

                          {/* Text */}
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <p className="text-xs font-medium truncate leading-tight">{script.title}</p>
                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                              {isFree ? (
                                <span className="inline-flex items-center h-3.5 px-1.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white leading-none shrink-0">FREE</span>
                              ) : isPurchased ? (
                                <Badge variant="outline" className="h-3.5 px-1 text-[9px] shrink-0 border-primary/40 text-primary leading-none">{t("owned")}</Badge>
                              ) : (
                                <Badge variant="outline" className="h-3.5 px-1 text-[9px] shrink-0 leading-none">{formatPrice(script.price_cents)}</Badge>
                              )}
                              {isActiveInline && (
                                <Badge variant="outline" className="h-3.5 px-1 text-[9px] shrink-0 border-primary/50 text-primary leading-none">{t("loaded")}</Badge>
                              )}
                              {script.creator_username && (
                                <span className="text-[10px] text-muted-foreground truncate">{t("byCreatorUsername", { creatorUsername: script.creator_username })}</span>
                              )}
                              {script.avg_rating > 0 && (
                                <span className="flex items-center gap-0.5 shrink-0 text-[10px] text-muted-foreground">
                                  <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
                                  {script.avg_rating.toFixed(1)}
                                </span>
                              )}
                              {script.purchase_count > 0 && (
                                <span className="flex items-center gap-0.5 shrink-0 text-[10px] text-muted-foreground">
                                  <ShoppingBag className="h-2.5 w-2.5" />
                                  {script.purchase_count}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Icon-only buttons */}
                          <div className="flex items-center gap-1 shrink-0 ml-auto">
                            {isPurchased ? (
                              <>
                                <Button
                                  size="icon"
                                  variant={isActiveInline && globalCurrentScriptId === script.id ? "secondary" : "default"}
                                  className="h-7 w-7"
                                  title={isActiveInline && globalCurrentScriptId === script.id ? (globalIsPlaying ? t("pause") : t("resume")) : t("playHere")}
                                  disabled={inlineLoading === script.id}
                                  onClick={() => handlePlayHere(script)}
                                >
                                  {inlineLoading === script.id
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : isActiveInline && globalCurrentScriptId === script.id && globalIsPlaying
                                    ? <Pause className="h-3 w-3" />
                                    : <Play className="h-3 w-3" />}
                                </Button>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="h-7 w-7"
                                  title={t("openInScriptPlayer")}
                                  onClick={() => handleOpenFullPlayer(script)}
                                  disabled={isOpeningFullPlayer}
                                >
                                  {isOpeningFullPlayer
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <ExternalLink className="h-3 w-3" />}
                                </Button>
                              </>
                            ) : (
                              <>
                                {script.trial_duration_seconds > 0 && (
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="h-7 w-7"
                                    title={t("preview")}
                                    onClick={() => setDetailScript(script)}
                                  >
                                    <PlayCircle className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button
                                  size="icon"
                                  className="h-7 w-7"
                                  title={isFree ? t("getFree") : t("buy", { price: formatPrice(script.price_cents) })}
                                  onClick={() => setDetailScript(script)}
                                >
                                  <ShoppingCart className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <HeatmapStrip samples={script.intensity_samples ?? []} />
                        {/* Playback seeker — grows below heatmap when this script is active */}
                        {isActiveInline && <ScriptSeeker scriptId={script.id} />}
                      </div>
                    )
                  })}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Local Scripts Tab */}
          <TabsContent value="local" className="mt-3">
            {/* Storage usage bar — only shown to free users */}
            {!userIsPremium && totalScriptCount >= FREE_SCRIPT_LIMIT * 0.8 && (
              <div className="mb-3 px-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>{t("totalScriptCountFREESCRIPTLIMITScripts", { totalScriptCount, fREESCRIPTLIMIT: FREE_SCRIPT_LIMIT })}</span>
                  {totalScriptCount >= FREE_SCRIPT_LIMIT ? (
                    <a href="/subscribe" className="text-amber-500 hover:underline font-medium">{t("upgradeToUnlockMore")}</a>
                  ) : (
                    <span>{t("valueRemaining", { value: FREE_SCRIPT_LIMIT - totalScriptCount })}</span>
                  )}
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    ref={scriptUsageBarRef}
                    className={cn(
                      "h-full rounded-full transition-all",
                      totalScriptCount >= FREE_SCRIPT_LIMIT ? "bg-destructive" :
                      totalScriptCount >= FREE_SCRIPT_LIMIT * 0.9 ? "bg-amber-500" : "bg-primary"
                    )}
                  />
                </div>
              </div>
            )}
            {/* Only show unpublished local drafts — published scripts are already shown via ownMarketScripts */}
            {(() => {
              const draftScripts = localScripts.filter((s) => !s.isPublished)
              const hasAny = ownMarketScripts.length > 0 || draftScripts.length > 0
              if (!hasAny) return (
                <div className="text-center py-8 px-2">
                  <ScrollText className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                  <p className="text-sm text-muted-foreground mb-4">{t("noScriptsOfYoursForThisContent")}</p>
                  <Button size="sm" onClick={onCreateNew} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    {t("createScript")}
                  </Button>
                </div>
              )
              return (
                <ScrollArea className="max-h-72 w-full">
                  <div className="divide-y rounded-xl border bg-background/30 overflow-hidden w-full">
                    {ownMarketScripts.map((script) => {
                      const isActiveInline = effectiveActiveInlineScriptId === script.id
                      const isOpeningFullPlayer = fullPlayerLoadingId === script.id
                      const isFree = script.price_cents === 0
                      return (
                        <div
                          key={script.id}
                          className={cn(
                            "flex flex-col w-full overflow-hidden transition-colors hover:bg-accent/35",
                            isActiveInline && "bg-primary/10"
                          )}
                        >
                          <div className="flex items-center gap-2 px-2 py-2">
                            {/* thumbnail */}
                            <div className="h-10 w-10 shrink-0 rounded overflow-hidden bg-muted">
                              {(script.thumbnail_url || thumbnailUrl) ? (
                                <img src={script.thumbnail_url ?? thumbnailUrl} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <ScrollText className="h-3.5 w-3.5 text-muted-foreground/40" />
                                </div>
                              )}
                            </div>

                            {/* text */}
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <p className="text-xs font-medium truncate leading-tight">{script.title}</p>
                              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                <span className="inline-flex items-center h-3.5 px-1.5 rounded-full text-[9px] font-bold bg-primary text-primary-foreground leading-none shrink-0">PUB</span>
                                {isFree ? (
                                  <span className="inline-flex items-center h-3.5 px-1.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white leading-none shrink-0">FREE</span>
                                ) : (
                                  <Badge variant="outline" className="h-3.5 px-1 text-[9px] shrink-0 leading-none">{formatPrice(script.price_cents)}</Badge>
                                )}
                                {script.avg_rating > 0 && (
                                  <span className="flex items-center gap-0.5 shrink-0 text-[10px] text-muted-foreground">
                                    <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
                                    {script.avg_rating.toFixed(1)}
                                  </span>
                                )}
                                {script.purchase_count > 0 && (
                                  <span className="flex items-center gap-0.5 shrink-0 text-[10px] text-muted-foreground">
                                    <ShoppingBag className="h-2.5 w-2.5" />
                                    {script.purchase_count}
                                  </span>
                                )}
                                <span className="text-[10px] text-muted-foreground">{t("valueSAverageIntensityAvg", { value: Math.round((script.script_duration_ms || 0) / 1000), averageIntensity: script.average_intensity })}</span>
                              </div>
                            </div>

                            {/* buttons */}
                            <div className="flex items-center gap-1 shrink-0 ml-auto">
                              <Button
                                size="icon"
                                variant={isActiveInline && globalCurrentScriptId === script.id ? "secondary" : "default"}
                                className="h-7 w-7"
                                title={isActiveInline && globalCurrentScriptId === script.id ? (globalIsPlaying ? t("pause") : t("resume")) : t("play")}
                                disabled={inlineLoading === script.id}
                                onClick={() => handlePlayHere(script)}
                              >
                                {inlineLoading === script.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : isActiveInline && globalCurrentScriptId === script.id && globalIsPlaying
                                  ? <Pause className="h-3 w-3" />
                                  : <Play className="h-3 w-3" />}
                              </Button>
                              <Button size="icon" variant="outline" className="h-7 w-7" title={t("openInScriptPlayer")} onClick={() => handleOpenFullPlayer(script)} disabled={isOpeningFullPlayer}>
                                {isOpeningFullPlayer ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                              </Button>
                            </div>
                          </div>
                          <HeatmapStrip samples={script.intensity_samples ?? []} />
                          {/* Was a hand-rolled copy of ScriptSeeker that referenced
                              globalDuration / globalCurrentTime — neither exists in this
                              scope, so rendering it threw a ReferenceError. ScriptSeeker
                              (used identically at the grid-view branch above) owns its own
                              time subscription and already no-ops when it isn't the active
                              script, so the id check here is redundant. */}
                          {isActiveInline && <ScriptSeeker scriptId={script.id} />}
                        </div>
                      )
                    })}
                    {draftScripts.map((script) => (
                      <div
                        key={script.id}
                        className="flex items-center gap-2 px-2 py-2 w-full overflow-hidden transition-colors hover:bg-accent/35"
                      >
                        {/* thumbnail */}
                        <div className="h-10 w-10 shrink-0 rounded overflow-hidden bg-muted">
                          {(script.thumbnailUrl || thumbnailUrl) ? (
                            <img src={script.thumbnailUrl ?? thumbnailUrl} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center">
                              <ScrollText className="h-3.5 w-3.5 text-muted-foreground/40" />
                            </div>
                          )}
                        </div>

                        {/* text */}
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <p className="text-xs font-medium truncate leading-tight">{script.name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Badge variant="outline" className="h-3.5 px-1 text-[9px] shrink-0 text-muted-foreground leading-none">{t("draft")}</Badge>
                            <span className="text-[10px] text-muted-foreground">{t("valueSAverageIntensityAvg", { value: Math.round((script.funscript.metadata?.duration || 0) / 1000), averageIntensity: script.averageIntensity })}</span>
                          </div>
                        </div>

                        {/* buttons */}
                        <div className="flex items-center gap-1 shrink-0 ml-auto">
                          <Button
                            size="icon"
                            variant="ghost"
                            className={cn("h-7 w-7", favoriteScriptIds.includes(script.id) && "text-amber-500")}
                            title={favoriteScriptIds.includes(script.id) ? t("removeFromFavs") : t("addToFavs")}
                            onClick={() => toggleFavoriteScript(script.id)}
                          >
                            <Heart className={cn("h-3 w-3", favoriteScriptIds.includes(script.id) && "fill-current")} />
                          </Button>
                          <Button size="icon" className="h-7 w-7" title={t("play")} onClick={() => onPlay(script)}>
                            <Play className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )
            })()}
          </TabsContent>

          {/* Device Control Tab */}
          <TabsContent value="manual" className="mt-3">
            <div className="text-center py-8">
              <SlidersHorizontal className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground mb-1">{t("controlYourDeviceWhileWatching")}</p>
              <p className="text-xs text-muted-foreground mb-4">
                {t("useTheXYPadOverlayToAdjustSpeedAndStrok")}
              </p>
              <Button size="sm" onClick={onManualControl}>
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                {t("openDeviceControl")}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Script detail/purchase modal */}
      {detailScript && (
        <CommunityScriptDetailModal
          script={detailScript}
          open={!!detailScript}
          onOpenChange={(open) => {
            if (!open) {
              // Re-check ownership after modal closes (user may have purchased)
              const id = detailScript.id
              setDetailScript(null)
              fetch(`/api/marketplace/scripts/${id}/ownership`)
                .then((r) => r.ok ? r.json() : { owned: false })
                .then((d) => { if (d.owned) setPurchasedIds((prev) => new Set([...prev, id])) })
                .catch(() => {})
            }
          }}
        />
      )}
    </>
  )
}
